import React, { useEffect, useState } from 'react';
import { Text, TextVariant } from '@naf/text';
import { UserInteractionVariant } from '@naf/user-interaction';
import { Button } from '@naf-simba/elements-buttons';
import { Loader } from '@naf-simba/building-blocks-loaders';
import { Input } from '@naf/input';
import { Link } from '@naf-simba/elements-links';
import { GridCol } from '@naf/grid';
import { BlockContentContext } from '@components/block-content/BlockContent.utils';
import { ProductType, useProductChangeCalculator } from '../../../../../../hooks/useProductChangeCalculator';
import { useSessionStorage } from '../../../../../../hooks/useSessionStorage';
import { OilChangeCalculations } from '../../../../../../../../types/oilChangeCalculationTypes';
import { ArticleBlockContentGridCol } from '../ArticleBlockContentGridCol';
import useSelector from '../../../../../../redux/typedHooks';
import { normalizeLicensePlate } from '../../../../../../utils/normalizeLicensePlate';
import * as S from './OilChangeCalculator.styles';

export const OilChangeCalculatorBlock = () => {
  const [centerId] = useSessionStorage('centerId', undefined);
  const [licensePlateNumber, setLicensePlateNumber] = useState<string>('');
  const [calculationResult, setCalculationResult] = useState<OilChangeCalculations | undefined>();
  const { calculate, calculation, isLoading, error } = useProductChangeCalculator({
    productType: ProductType.OILCHANGE,
    licensePlateNumber,
    centerId: centerId as string | undefined,
  });

  const isMember = useSelector((state) => state?.myMembership?.customerInformation?.data?.membership?.isValid ?? false);

  useEffect(() => {
    setCalculationResult(calculation as OilChangeCalculations);
  }, [calculation]);

  const getPriceComponents = () => {
    if (!calculationResult) return null;
    const memberPrice = calculationResult.oilChange?.totalPriceForMembers ?? 0;
    const nonMemberPrice = calculationResult.oilChange?.totalPriceForNonMembers ?? 0;

    if (memberPrice === nonMemberPrice || memberPrice > nonMemberPrice) {
      return <Text variant={TextVariant.Header3}>Pris for oljeskift: kr {nonMemberPrice},-</Text>;
    }

    return (
      <>
        <Text variant={TextVariant.Header3}>
          {isMember ? `Din pris: kr ${memberPrice.toFixed(0)},-` : ''}
          {!isMember ? `Pris for medlemmer: kr ${memberPrice.toFixed(0)},-` : ''}
        </Text>
        <Text variant={TextVariant.Header3}>Pris for ikke-medlemmer: kr {nonMemberPrice},-</Text>
      </>
    );
  };

  return (
    <S.OilChange>
      {calculationResult?.oilChange?.isCampaign && <S.StyledTag color="secondary" shade="light" text="Kampanje" />}
      <Text variant={TextVariant.Header2}>Se hvor mye oljeskift på din bil koster</Text>
      <Text variant={TextVariant.ArticleTextLarge}>
        Legg inn registeringsnummer for å beregne hvor mye oljeskiftet koster for din bil.
      </Text>
      <S.Search>
        <Input
          placeholder="Registreringsnummer"
          id="registrationNumber"
          type="text"
          onChange={(number: string) => {
            const normalized = normalizeLicensePlate(number);
            setLicensePlateNumber(normalized);
          }}
          onKeyUp={(e: KeyboardEvent) => {
            if (e.key === 'Enter') calculate();
          }}
        />
        <S.StyledButton type="button" color="secondary" onClick={() => calculate()}>
          Beregn pris
        </S.StyledButton>
      </S.Search>
      {isLoading && (
        <S.SpinnerWrapper>
          <Loader />
        </S.SpinnerWrapper>
      )}
      {error && !isLoading && (
        <S.LookupResultError>
          <S.StyledUserInteraction variant={UserInteractionVariant.Error}>
            Vi klarte ikke finne en bil på dette registreringsnummeret.
          </S.StyledUserInteraction>
          <Text>Ta kontakt med kundeservice for en prisforespørsel på oljeskift for ditt kjøretøy.</Text>
          <Link to="/kundeservice" text="Kontakt kundeservice" />
        </S.LookupResultError>
      )}
      {!error && !isLoading && calculationResult && (
        <S.LookupResultSuccess>
          <S.VehicleText>
            <S.StyledCheckCircle size={22} />
            {calculationResult.vehicle && (
              <S.SuccessText variant={TextVariant.BodyText}>
                {calculationResult.vehicle?.manufactureName} {calculationResult.vehicle?.modelName}{' '}
                {calculationResult.vehicle?.year}
              </S.SuccessText>
            )}
          </S.VehicleText>
          {calculationResult.oilChange && calculationResult.oilChange.litersOfOil && (
            <S.Summary>
              {getPriceComponents()}
              <S.SummaryRow>
                <GridCol s="8" m="8" l="8" xl="8">
                  <Text variant={TextVariant.ArticleTextHeader}>
                    {calculationResult.oilChange?.litersOfOil} liter olje
                  </Text>
                </GridCol>
                <GridCol s="4" m="4" l="4" xl="4">
                  <Text>{calculationResult.oilChange?.pricePerLiterOfOil},-/liter</Text>
                </GridCol>
              </S.SummaryRow>
              <S.SummaryRow>
                <GridCol s="8" m="8" l="8" xl="8">
                  <Text variant={TextVariant.ArticleTextHeader}>Arbeid</Text>
                </GridCol>
                <GridCol s="4" m="4" l="4" xl="4">
                  <S.Row>
                    {isMember && <Text>{calculationResult.oilChange?.laborPriceForMembers},-</Text>}
                    <Text
                      style={{
                        textDecoration: isMember ? 'line-through' : 'none',
                      }}
                    >
                      {calculationResult.oilChange?.laborPriceForNonMembers},-
                    </Text>
                  </S.Row>
                </GridCol>
              </S.SummaryRow>
              <S.SummaryRow>
                <GridCol s="8" m="8" l="8" xl="8">
                  <Text variant={TextVariant.ArticleTextHeader}>Oljefilter</Text>
                </GridCol>
                <GridCol s="4" m="4" l="4" xl="4">
                  <Text>{calculationResult.oilChange?.oilFilterPrice},-</Text>
                </GridCol>
              </S.SummaryRow>
            </S.Summary>
          )}
          {!calculationResult.oilChange?.litersOfOil &&
            calculationResult.vehicle &&
            (calculationResult.vehicle.isElectric ? (
              <>
                <S.StyledUserInteraction variant={UserInteractionVariant.Error}>
                  Det ser ikke ut som din bil er aktuell for oljeskift.
                </S.StyledUserInteraction>
                <Text>
                  Har det skjedd en feil? Ta kontakt med kundeservice for en prisforespørsel på oljeskift for ditt
                  kjøretøy
                </Text>
                <Link to="/kundeservice" text="Kontakt kundeservice" />
              </>
            ) : (
              <>
                <Text variant={TextVariant.Header3}>Vi klarer ikke å kalkulere riktig pris for din bil</Text>
                <Text>Ta kontakt med kundeservice for en prisforespørsel på oljeskift for ditt kjøretøy</Text>
                <Link to="/kundeservice" text="Kontakt kundeservice" />
              </>
            ))}
          {calculationResult.link && calculationResult.link.data && (
            <Button
              href={calculationResult.link.data.href}
              target={calculationResult.link.data.blank ? '_blank' : '_self'}
            >
              Finn ledig tid
            </Button>
          )}
        </S.LookupResultSuccess>
      )}
    </S.OilChange>
  );
};

export const OilChangeCalculatorSerializer = (context: BlockContentContext = BlockContentContext.Stripped) => {
  if (context === BlockContentContext.Article) {
    return () => (
      <ArticleBlockContentGridCol>
        <OilChangeCalculatorBlock />
      </ArticleBlockContentGridCol>
    );
  }
  return () => <OilChangeCalculatorBlock />;
};
